import React, { useState } from 'react';
import Button from '@app/react-common/components/button/Button';
import Divider from '@app/react-common/components/divider/Divider';
import Input from '@app/react-common/components/input/Input';
import RadioButtonGroup, { RadioButtonOption } from '@app/react-common/components/radio-button-group/RadioButtonGroup';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { SelectedMethodInfo } from '../BulkEditModal';
import { Shippings } from '@app/pages/checkout-app/types';
import { useDuplicateNameValidation } from '../hooks/useDuplicateNameValidation';
import { NOTE_ACTIONS } from '../utils/consts';

type NoteAction = 'change' | 'remove';

interface NameDescriptionEditViewProps {
  onBack: () => void;
  onClose: () => void;
  onPreviewChanges: (data: NameDescriptionFormData) => void;
  selectedMethods: SelectedMethodInfo[];
  allShippingMethods?: Shippings[];
}

export interface NameDescriptionFormData {
  name: string;
  noteAction: NoteAction;
  note: string;
}

const NOTE_OPTIONS: RadioButtonOption[] = [
  { label: 'Change Note', value: NOTE_ACTIONS.CHANGE },
  { label: 'Remove Note Completely', value: NOTE_ACTIONS.REMOVE },
];

export const NameDescriptionEditView = ({
  onBack,
  onClose,
  onPreviewChanges,
  selectedMethods,
  allShippingMethods = [],
}: NameDescriptionEditViewProps) => {
  const [name, setName] = useState('');
  const [noteAction, setNoteAction] = useState<NoteAction>(NOTE_ACTIONS.CHANGE);
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { validateNameDescription } = useDuplicateNameValidation({
    selectedMethods,
    allShippingMethods,
  });

  const hasAnyMethodWithNote = selectedMethods.some((method) => method.note && method.note.trim() !== '');

  const isPreviewEnabled = (): boolean => {
    // Enable if name is provided
    if (name.trim() !== '') return true;

    // Enable if note action is "change" and note text is provided
    if (noteAction === NOTE_ACTIONS.CHANGE && note.trim() !== '') return true;

    // Enable if note action is "remove" and at least one method has a note to remove
    if (noteAction === NOTE_ACTIONS.REMOVE && hasAnyMethodWithNote) return true;

    return false;
  };

  const handlePreviewChanges = () => {
    const validationResult = validateNameDescription({ name, noteAction, note });

    if (!validationResult.valid) {
      setValidationError(validationResult.error || 'Validation failed');
      return;
    }

    setValidationError(null);
    onPreviewChanges({
      name,
      noteAction,
      note,
    });
  };

  const handleNoteActionChange = (option: RadioButtonOption) => {
    setNoteAction(option.value as NoteAction);
    setValidationError(null);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setValidationError(null);
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNote(e.target.value);
    setValidationError(null);
  };

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header">
        <div className="bulk-edit-modal__header__left">
          <Button icon="back" variant="ghost" size="small" onClick={onBack} />
          <h2>Name & Description</h2>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        {validationError && (
          <>
            <Divider />
            <div className="bulk-edit-modal__alert bulk-edit-modal__alert--danger bulk-edit-modal__alert--no-border">
              <img src="/assets/icons/error-icon.svg" alt="" />
              <span>{validationError}</span>
            </div>
          </>
        )}
        <div className="bulk-edit-modal__field">
          <label className="bulk-edit-modal__label">Name Of Delivery Method</label>
          <Input type="text" value={name} onChange={handleNameChange} placeholder="Name..." />
          <span className="bulk-edit-modal__helper-text">Leave empty to keep current names</span>
        </div>

        <div className="bulk-edit-modal__field">
          <label className="bulk-edit-modal__label">Note On Delivery Method</label>
          <RadioButtonGroup
            options={NOTE_OPTIONS}
            defaultOptionValue="change"
            onChange={handleNoteActionChange}
            disabled={false}
          />
          {noteAction === NOTE_ACTIONS.CHANGE && (
            <>
              <Input
                type="text"
                value={note}
                onChange={handleNoteChange}
                placeholder="e.g. Free for orders over $50..."
              />
              <span className="bulk-edit-modal__helper-text">Leave empty to keep current notes</span>
            </>
          )}
          {noteAction === NOTE_ACTIONS.REMOVE && (
            <>
              <Divider />
              <div className="bulk-edit-modal__alert bulk-edit-modal__alert--warning bulk-edit-modal__alert--no-border">
                <img src="/assets/icons/banner-attention-icon.svg" alt="" />
                <span>
                  By choosing this option, all of the selected shipping method notes will be removed completely.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {!isPreviewEnabled() ? (
          <Tooltip
            tooltipText={
              noteAction === NOTE_ACTIONS.REMOVE && !hasAnyMethodWithNote
                ? 'All selected methods already have no notes'
                : 'Enter a name or note to preview changes'
            }
            maxWidth={220}>
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" disabled />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" />
          </div>
        )}
      </div>
    </div>
  );
};
